import { FC, ReactElement, ReactNode, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Stack, Typography } from '@mui/material';
import * as Sentry from '@sentry/capacitor';
import { getAuth } from 'firebase/auth';

import { useBusinessLayer } from '@mopla/business-logic';
import { MplButton } from '@mopla/ui';
import { usePersistentCountdownGuard } from '@mopla/utils';

import { Bold } from './VerifyFailureScreen.styles';

interface IVerifyFailureScreenProps {
	onResendSuccess: VoidFunction;
	render: (p: { content: ReactNode; submit: ReactNode }) => ReactElement;
}

export const VerifyFailureScreen: FC<IVerifyFailureScreenProps> = ({
	onResendSuccess,
	render,
}) => {
	const { api } = useBusinessLayer();
	const { t } = useTranslation('verify');
	const { reinitCountdownGuard } = usePersistentCountdownGuard('emailSendTime');
	const [isLoading, setLoading] = useState(false);

	const auth = getAuth();

	const resendEmail = async () => {
		setLoading(true);

		try {
			await api.post('/api/command/verifyEmail', { app: 'PASSENGER' });

			reinitCountdownGuard();
			onResendSuccess();
		} catch (e) {
			Sentry.captureException(e);
		} finally {
			setLoading(false);
		}
	};

	return render({
		content: (
			<Stack>
				<Typography
					variant="b1"
					color="primary.main"
					mb="24px"
					data-testid="verify-email-failure-screen-title"
				>
					{t('text.title.link_expired')}
				</Typography>
				<Typography variant="b1" color="primary.main">
					<Trans
						i18nKey="verify:text.title.nothing_worry"
						values={{ regEmail: auth?.currentUser?.email }}
						components={{ bold: <Bold /> }}
					/>
				</Typography>
			</Stack>
		),
		submit: (
			<MplButton
				fullWidth
				variant="outlined"
				color="primaryButtonV2"
				onClick={resendEmail}
				showLoader={isLoading}
				disabled={isLoading}
				data-testid="verify-email-failure-screen-resend-button"
			>
				{t('button.resend_email')}
			</MplButton>
		),
	});
};
